import React, { ChangeEvent } from 'react';

import Observer from '@researchgate/react-intersection-observer';
import cx from 'classnames';
import { Code, Icon as IconPhosphor, Link } from 'phosphor-react';

import { Alert } from 'components/shared/Alert';
import Button from 'components/shared/Button';
import Input from 'components/shared/FormAdapters/FormDefault/Input';
import TextArea from 'components/shared/FormAdapters/FormDefault/TextArea';
import Icon from 'components/shared/Icon';
import Close from 'components/shared/Icon/icons/Close';
import ErrorCircle from 'components/shared/Icon/icons/ErrorCircle';
import { TabSelector, TabSelectorProps } from 'components/shared/TabSelector';
import Text from 'components/shared/Typography/Text';
import { PLEASE_ENTER_CORRECT_HTML, PLEASE_ENTER_CORRECT_LINK } from 'constants/texts';
import { ISourceBlockData } from 'detailsv2/__blocks';
import { showErrorSnackbar } from 'hooks/useSnackbar';
import { isRidewithgps } from 'utils/parseIframeUrl';

import { useBlockContext } from '../../Block/context';
import { createHtmlSource, createUrlSource, isBlockedDomain, validateUrl } from './functions';
import styles from './index.module.css';
import * as t from './types';

export function IframeBlock() {
  const { block: data, onAdd } = useBlockContext();
  const { source } = data?.data as ISourceBlockData;
  const isShowInsertModal = source === undefined;

  if (isShowInsertModal) {
    return <InsertEmbededCodeModal onAdd={onAdd as any} onClose={() => {}} />;
  }

  return <Iframe {...(data?.data as any)} />;
}

export default IframeBlock;

export function Iframe({ source, height, type }: t.ShowIframeProps) {
  const [isInViewport, setIsInViewport] = React.useState(false);

  const handleIntersection = (event: IntersectionObserverEntry, unobserve: () => void) => {
    if (event.isIntersecting) {
      setIsInViewport(true);
      unobserve();
    }
  };

  const renderIframe = () => {
    if (type) {
      return (
        <iframe
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allow="clipboard-write"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin"
          srcDoc={source}
          className={styles.newIframe}
          style={{ height: height ? `${height}px` : '400px' }}
          title="Secure Iframe"
        />
      );
    }

    return (
      <iframe
        title="source"
        allowFullScreen
        src={source}
        style={{ border: 'none' }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allow="clipboard-write"
      />
    );
  };

  return (
    <Observer onChange={handleIntersection}>
      <div className={cx(styles.frame, { [styles['frame-clear']]: isRidewithgps(source || '') })}>
        {isInViewport && renderIframe()}
      </div>
    </Observer>
  );
}

type Props = { Icon: IconPhosphor; text: string };

const Label = (props: Props) => (
  <div className={styles.tab}>
    <props.Icon size={22} className={styles.tabIcon} />
    <Text className={styles.tabText} size="regular" type="brandon">
      {props.text}
    </Text>
  </div>
);

const LinkLabel = () => <Label Icon={Link} text="Enter Link" />;
const EmbedCodeLabel = () => <Label Icon={Code} text="Embed Code" />;

const LinkContnent = (props: t.TabContentProps) => {
  const [isError, setIsError] = React.useState(false);
  const [urlInputValue, setUrlInputValue] = React.useState('');
  const [height, setHeight] = React.useState(400);

  const addCode = async (value: string) => {
    try {
      const trimmedValue = value.trim();

      if (isBlockedDomain(trimmedValue)) {
        setIsError(true);
        return;
      }

      if (!validateUrl(trimmedValue)) {
        setIsError(true);
        return;
      }
      const source = createUrlSource(trimmedValue);

      if (!source) {
        setIsError(true);
        throw new Error('Invalid source');
      }

      props.onAdd && (await props.onAdd({ type: 'iframe', data: { source, height, type: 'link' } }));
      props.onClose && props.onClose();
    } catch (e) {
      showErrorSnackbar("We couldn't add the code to the block");
      console.error('----- addCode e', e);
    }
  };

  return (
    <>
      <div className={styles.content}>
        <div>
          <Text type="brandon" size="default" className={styles.subtitle}>
            If you are embedding Google Maps, please use the “Embed Code” tab.
          </Text>

          <TextArea
            placeholder="Paste Link"
            value={urlInputValue}
            isError={isError}
            onChange={(e) => setUrlInputValue(e.currentTarget.value)}
            className={styles.textArea}
          ></TextArea>
          {isError && (
            <Text className={styles.errorText} accent type="danger" size="small">
              <ErrorCircle />
              {PLEASE_ENTER_CORRECT_LINK}
            </Text>
          )}

          <Text type="brandon" size="default" className={styles.heightInputTitle}>
            This field sets the height (in pixels) for the iFrame block. Recommended value: 400
          </Text>

          <div>
            <Input
              value={height}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const value = Number(e.target.value) || 0;
                const handledValue = (() => {
                  if (value > 2000) return 2000;
                  return value;
                })();
                setHeight(handledValue);
              }}
              floatingPlaceholder="Iframe Height"
            />
          </div>
        </div>

        <Alert
          variant="info"
          content="Please note that some websites cannot be embedded due to their restrictions."
          className={styles.alert}
        />
      </div>
      <div className={styles.footer}>
        <Button size="medium" disabled={urlInputValue.length === 0} onClick={() => addCode(urlInputValue)}>
          Insert
        </Button>
      </div>
    </>
  );
};

const EmbedCodeContnent = (props: t.TabContentProps) => {
  const [isError, setIsError] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  const addCode = async (value: string) => {
    try {
      const trimmedValue = value.trim();
      const source = createHtmlSource(trimmedValue);

      if (isBlockedDomain(trimmedValue)) {
        setIsError(true);
        return;
      }

      if (!source) {
        setIsError(true);
        throw new Error('Invalid source');
      }

      props.onAdd && (await props.onAdd({ type: 'iframe', data: { source, type: 'code' } }));
      props.onClose && props.onClose();
    } catch (e) {
      showErrorSnackbar("We couldn't add the code to the block");
      console.error('----- addCode e', e);
    }
  };

  return (
    <>
      <div className={styles.content}>
        <div>
          <TextArea
            placeholder="Paste embed code"
            value={inputValue}
            isError={isError}
            onChange={(e) => setInputValue(e.currentTarget.value)}
            className={styles.textArea}
          ></TextArea>
          {isError && (
            <Text className={styles.errorText} accent type="danger" size="small">
              <ErrorCircle />
              {PLEASE_ENTER_CORRECT_HTML}
            </Text>
          )}
        </div>

        <Alert
          variant="info"
          content={`Please note that the code you paste here should look similar to this: 
<iframe src="{YOUR_URL}"></iframe>`}
          className={styles.alert}
        />
      </div>
      <div className={styles.footer}>
        <Button size="medium" disabled={inputValue.length === 0} onClick={() => addCode(inputValue)}>
          Insert
        </Button>
      </div>
    </>
  );
};

export function InsertEmbededCodeModal({ onAdd, onClose }: t.IIframeProps) {
  const tabs: TabSelectorProps['tabs'] = [
    {
      labelContent: <LinkLabel />,
      content: <LinkContnent onAdd={onAdd as any} onClose={onClose} />,
    },
    {
      labelContent: <EmbedCodeLabel />,
      content: <EmbedCodeContnent onAdd={onAdd as any} onClose={onClose} />,
    },
  ];

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Text type="brandon" isStrong weight="500">
          Insert iFrame
        </Text>
        <button
          type="button"
          className={cx(styles.modalClose, 'custom-modal-close-btn')}
          tabIndex={0}
          onClick={onClose}
        >
          <Icon component={Close} className={cx(styles.closeIcon, 'custom-modal-close-icon')} />
        </button>
      </div>

      <TabSelector tabsClassName={styles.tabsContainer} tabs={tabs} />
    </div>
  );
}
